<script setup lang="ts">

  import { LMarkerClusterGroup } from 'vue-leaflet-markercluster'

  const { locale, t } = useI18n()

  const props = defineProps({
    data: {
      type: Array<Country>,
      required: true
    },
    bound: Array,
    navigate: Function
  })
  
  const route = useRoute()
  const center: [number, number] = (route.query.latitude && route.query.longitude) ? [Number(route.query.latitude), Number(route.query.longitude)] : [0, 0]

  const deviceStore = useDeviceStore()
  const isMobile = deviceStore.isMobile

  const zoom = ref(isMobile ? 2 : 3)
  const tileLayerUrl = `http://{s}.google.com/vt/lyrs=m&hl=${locale.value}&x={x}&y={y}&z={z}`

  const mapRef: Ref<MapRef | null> = ref(null)

  let isDragging = ref(false)
  let startX = 0
  let startY = 0
  let endX = 0
  let endY = 0
  let isPanning = ref(false)

  const handleMouseMove = (event: Mouse) => {
    if (isDragging.value) {
      const deltaX = event.containerPoint.x - startX;
      const deltaY = event.containerPoint.y - startY;
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        isPanning.value = true;
      }
    }
  };
  const handleMouseDown = (event: Mouse) => {
    isDragging.value = true
    startX = event.containerPoint.x
    startY = event.containerPoint.y
    isPanning.value = false
  }

  const handleMouseUp = (event: Mouse) => {
    isDragging.value = false
    endX = event.containerPoint.x
    endY = event.containerPoint.y
  }

  const handleNavigate = (destination: Destination) => {
    if(!isPanning.value && props.navigate){
      props.navigate(destination)
    }
  }

  const boundMap = (arrayLatitudes: [number, number][]) => {
    if(arrayLatitudes.length === 0) return
    // @ts-ignore
    if(arrayLatitudes.length === 1) mapRef.value?.leafletObject?.setView(arrayLatitudes[0], 7)
    mapRef.value?.leafletObject?.fitBounds(arrayLatitudes)
  }

  const handleReady = () => {
    if (props.bound?.length) {
      boundMap(props.bound as [number, number][])
    }
  }

  onBeforeUnmount(() => {
    if (mapRef.value?.leafletObject) {
      const map = mapRef.value.leafletObject as any
      
      try {
        map.off()
        map.eachLayer((layer: any) => {
          if (layer.remove) {
            layer.remove()
          }
        })
        map.stop()
      } catch (error) {
        console.warn('Error cleaning up Leaflet map:', error)
      }
      
      mapRef.value = null
    }
  })

</script>

<template>
  <l-map
    class="w-full h-full z-0 rounded-3xl"
    ref="mapRef"
    :zoom="zoom"
    :options="{zoomControl: false, attributionControl: false}"
    :center="center"
    :useGlobalLeaflet="true"
    @ready="handleReady"
    @mousemove="handleMouseMove($event)"
    @mousedown="handleMouseDown($event)"
    @mouseup="handleMouseUp($event)"
  >
    <l-tile-layer
      :url="tileLayerUrl"
      :maxZoom= "18"
      :subdomains="['mt0', 'mt1', 'mt2', 'mt3']"
    />
    <template v-if="isMobile">
      <l-marker-cluster-group
        :showCoverageOnHover="false"
      >
        <template v-for="city in data ?? []">
          <l-marker :lat-lng="[parseFloat(city?.location?.split(',')[0]), parseFloat(city?.location?.split(',')[1])]" @click="handleNavigate(city)" class="flex">
            <l-icon class="flex">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6 -ml-1.5 -mt-3 text-3xl">
                <path fill-rule="evenodd" d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd" />
              </svg>
              <div v-if="isMobile" class="text-xs w-fit rounded-lg bg-white/80 backdrop-blur-lg border border-neutral-200 p-2 py-1.5 absolute -top-5 left-6">
                <b class="whitespace-nowrap text-neutral-800">{{ city.name }}</b>:<div class="whitespace-nowrap text-neutral-600">{{ city.numHotels }} {{ t('labels.hoteles') }}</div>
              </div>
            </l-icon>
            <l-tooltip v-if="!isMobile" class="bg-white/80 backdrop-blur-lg" :permanent="true"> 
              <p class="text-sm"><b>{{ city.name }}</b>: {{ city.numHotels }} {{ t('labels.hoteles') }}</p>
            </l-tooltip>
          </l-marker>
        </template>
      </l-marker-cluster-group>
    </template>
    
    <template v-else>
      <template v-for="city in data ?? []">
      <l-marker :lat-lng="[parseFloat(city?.location?.split(',')[0]), parseFloat(city?.location?.split(',')[1])]" @click="handleNavigate(city)" class="flex">
        <l-icon class="flex">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6 -ml-1.5 -mt-3 text-3xl">
            <path fill-rule="evenodd" d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd" />
          </svg>
          <div v-if="isMobile" class="text-xs w-fit rounded-lg bg-white/80 backdrop-blur-lg border border-neutral-200 p-2 py-1.5 absolute -top-5 left-6">
            <b class="whitespace-nowrap text-neutral-800">{{ city.name }}</b>:<div class="whitespace-nowrap text-neutral-600">{{ city.numHotels }} {{ t('labels.hoteles') }}</div>
          </div>
        </l-icon>
        <l-tooltip v-if="!isMobile" class="bg-white/80 backdrop-blur-lg" :permanent="true"> 
          <p class="text-sm"><b>{{ city.name }}</b>: {{ city.numHotels }} {{ t('labels.hoteles') }}</p>
        </l-tooltip>
      </l-marker>
    </template>
    </template>
  </l-map>
</template>

<style lang="css">

  .leaflet-div-icon {
    background-color: transparent;
    border: none;
  }

  .marker-cluster-large,
  .marker-cluster-medium,
  .marker-cluster-small {
    background-color: #1F2937;
    color: #fff;
  }

  .marker-cluster-large div,
  .marker-cluster-medium div,
  .marker-cluster-small div {
    background-color: #1F2937;
    font-weight: bold;
  }

</style>